import React, { useRef } from 'react';
import Button from '@atlaskit/button';
import ButtonGroup from '@atlaskit/button/button-group';
import EmptyState from '@atlaskit/empty-state';
import FeedbackIcon from '@atlaskit/icon/glyph/feedback';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import BreadcrumbRenderer from '@atlassian/jira-breadcrumbs/src/breadcrumb-renderer/index.tsx';
import ProjectBreadcrumb from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/project/index.tsx';
import {
	determineProductCrossSellConfig,
	LICENSED_PRODUCTS,
} from '@atlassian/jira-common-util-get-tenant-context';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import ReportErrors from '@atlassian/jira-errors-handling/src/utils/reporting-error-boundary.tsx';
import { ENTRYPOINT_ID_JIRA_SOFTWARE_PROJECT } from '@atlassian/jira-feedback-collector/src/constants.tsx';
import { useFeedbackCollectorActions } from '@atlassian/jira-feedback-collector/src/controllers/index.tsx';
import { DocumentTitle } from '@atlassian/jira-global-document-title/src';
import { PageTopBar } from '@atlassian/jira-horizontal-nav-page-topbar/src/ui/index.tsx';
import { FormattedMessage } from '@atlassian/jira-intl';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { getWillShowNav3 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav3.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { createAri } from '@atlassian/jira-platform-ari';
import { ScheduleConfigurationProvider } from '@atlassian/jira-platform-opsgenie-schedule-timeline/src/services/schedule-configuration/index.tsx';
import {
	ContextualAnalyticsData,
	FireTrackAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useAssociatedOpsgenieTeamQueryAGG } from '@atlassian/jira-project-opsgenie-use-associated-opsgenie-team/src/services/index.tsx';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller/src/components/licensed-products/index.tsx';
import { Redirect } from '@atlassian/react-resource-router';
import { FEEDBACK_COLLECTOR_LOCATION_VALUE } from '../common/constant';
import messages from '../common/messages';
import { useOpsGenieScheduleFeatureState } from '../common/state/main';
import { opsgenieScheduleFeatureStates } from '../common/state/types';
import OpsgenieXflowSvg from './assets/megaphone-opsgenie-xflow.svg';
import ExampleScheduleComponent from './components/example-schedule-component';
import MoreMenu, { type ExpandMode } from './components/more-menu';
import OpsgenieScheduleComponent from './components/opsgenie-schedule-component';
import TryItNowComponent from './components/try-it-now-component';

type Props = {
	isAdmin: boolean;
	isPreExpand: boolean;
	isProjectAdmin: boolean;
	baseUrl: string;
	cloudId: string;
	projectKey: string;
	projectId: string | number;
	projectName: string;
	projectType: string;
	simplified: boolean;
	environment: string | undefined | null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onLinkOpsgenieTeamClick = (_: any, analyticsEvent: any) => {
	fireUIAnalytics(analyticsEvent, 'button clicked', 'linkOpsgenieTeamButton');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onGotoOpsgenieClick = (_: any, analyticsEvent: any) => {
	fireUIAnalytics(analyticsEvent, 'button clicked', 'gotoOpsgenieButton');
};

const renderConnectTeamDescription = (canLinkOpsgenieTeam: boolean) => {
	const descriptionMessage = canLinkOpsgenieTeam
		? messages.noOpsgenieTeamBodyAdmin
		: messages.noOpsgenieTeamBodyBasic;

	return (
		<Box as="p" xcss={descriptionLineStyles}>
			<FormattedMessage {...descriptionMessage} />
		</Box>
	);
};

const OpsgenieScheduleView = (props: Props) => {
	const { formatMessage } = useIntl();

	const {
		baseUrl,
		cloudId,
		isAdmin,
		isPreExpand,
		projectKey,
		projectId,
		projectName,
		simplified,
		isProjectAdmin,
	} = props;

	let expandMode: ExpandMode = 'none';

	if (isAdmin) {
		expandMode = isPreExpand ? 'pre' : 'post';
	} else if (isProjectAdmin && !isPreExpand) {
		expandMode = 'post';
	}
	const feedbackButtonRef = useRef<HTMLButtonElement>(null);

	const {
		data: associatedTeam,
		loading: isAssociatedOpsgenieTeamLoading,
		error: associatedOpsgenieTeamError,
	} = useAssociatedOpsgenieTeamQueryAGG(
		createAri({
			resourceOwner: 'jira',
			cloudId,
			resourceType: 'project',
			resourceId: `${projectId}`,
		}),
	);

	const { openFeedbackCollector } = useFeedbackCollectorActions();

	const onSubmitFeedbackClick = () => {
		openFeedbackCollector({
			entrypointId: ENTRYPOINT_ID_JIRA_SOFTWARE_PROJECT,
			feedbackGroupId: FEEDBACK_COLLECTOR_LOCATION_VALUE,
			triggerRef: feedbackButtonRef,
		});
	};

	let settingsUrl = '';
	if (simplified) {
		settingsUrl = `${baseUrl}/jira/software/projects/${projectKey}/settings/opsgenie`;
	} else {
		settingsUrl = `${baseUrl}/jira/software/c/projects/${projectKey}/settings/opsgenie`;
	}

	if (projectKey === undefined || projectId === undefined) {
		fireErrorAnalytics({
			meta: {
				id: 'onCallSchedule',
				packageName: 'jiraOpsgenieSchedule',
				teamName: 'fusion-solaris',
			},
			attributes: {
				projectId,
				isProjectKeyAvailable: projectKey !== undefined,
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	}

	const featureDisabled =
		// @ts-expect-error - TS2345 - Argument of type 'string | number' is not assignable to parameter of type 'string'.
		useOpsGenieScheduleFeatureState(parseInt(projectId, 10), simplified) ===
		opsgenieScheduleFeatureStates.DISABLED;
	const {
		[LICENSED_PRODUCTS.OPSGENIE]: hasOpsgenieLicense,
		[LICENSED_PRODUCTS.JIRA_SERVICEDESK]: hasServicedeskLicence,
	} = useLicensedProducts();
	// User can have OG by itself as a licenced product or bundled with JSM
	const hasOpsgenie = hasServicedeskLicence || hasOpsgenieLicense;
	const opsGenieNotConnectedAndNotCrossSell =
		determineProductCrossSellConfig() === 'disabled' && !hasOpsgenie;

	if (featureDisabled || opsGenieNotConnectedAndNotCrossSell) {
		return <Redirect to={`/projects/${projectKey}`} />;
	}
	const dataLoadingErrorAgg = () => {
		if (associatedOpsgenieTeamError) {
			fireErrorAnalytics({
				error: associatedOpsgenieTeamError,
				meta: {
					id: 'onCallScheduleTeamQueryAgg',
					packageName: 'jiraOpsgenieSchedule',
					teamName: 'fusion-solaris',
				},
				attributes: {
					endpoint: 'AssociatedOpsgenieTeamQuery',
					projectId,
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			return <p>{formatMessage(messages.opsgenieScheduleDataLoadingError)}</p>;
		}
		return null;
	};

	const showScheduleAgg = () => {
		if (!associatedTeam) {
			const canLinkOpsgenieTeam = isProjectAdmin || isAdmin;
			return (
				<ReportErrors
					id="onCallScheduleEmptyState"
					packageName="jiraOpsgenieSchedule"
					teamName="fusion-solaris"
					sendToPrivacyUnsafeSplunk
				>
					<FireTrackAnalytics
						eventName="emptyState viewed"
						actionSubjectId="noTeamsEmptyState"
						attributes={{
							simplified,
							canLinkOpsgenieTeam,
						}}
					/>
					{canLinkOpsgenieTeam ? (
						<EmptyState
							header={formatMessage(messages.noOpsgenieTeamTitle)}
							description={renderConnectTeamDescription(canLinkOpsgenieTeam)}
							imageUrl={OpsgenieXflowSvg}
							primaryAction={
								<Button appearance="primary" href={settingsUrl} onClick={onLinkOpsgenieTeamClick}>
									{formatMessage(messages.noOpsgenieTeamButtonAdmin)}
								</Button>
							}
						/>
					) : (
						<EmptyState
							header={formatMessage(messages.noOpsgenieTeamTitle)}
							description={renderConnectTeamDescription(canLinkOpsgenieTeam)}
							imageUrl={OpsgenieXflowSvg}
							primaryAction={
								<Button
									appearance="primary"
									href="https://app.opsgenie.com/"
									target="_blank"
									onClick={onGotoOpsgenieClick}
								>
									{formatMessage(messages.noOpsgenieTeamButtonBasic)}
								</Button>
							}
						/>
					)}
				</ReportErrors>
			);
		}
		return (
			<ReportErrors
				// @ts-expect-error - TS2571 - Object is of type 'unknown'.
				key={associatedTeam.opsgenieTeamId}
				id="onCallScheduleComponent"
				packageName="jiraOpsgenieSchedule"
				teamName="fusion-solaris"
				sendToPrivacyUnsafeSplunk
			>
				<ScheduleConfigurationProvider
					value={{
						interval: 2,
						intervalUnit: 'weeks',
						dateFormat: 'MM/D dd',
						currentDate: new Date().toISOString(),
					}}
				>
					<OpsgenieScheduleComponent
						{...{
							cloudId,
							baseUrl,
							// @ts-expect-error - TS2571 - Object is of type 'unknown'.
							teamId: associatedTeam.opsgenieTeamId,
							isAdmin,
							projectId,
						}}
					/>
				</ScheduleConfigurationProvider>
			</ReportErrors>
		);
	};

	const renderSchedule = () => {
		if (isPreExpand) return <ExampleScheduleComponent />;
		if (isAssociatedOpsgenieTeamLoading)
			return (
				<Flex justifyContent="center">
					{/* TODO: Refactor this to avoid the need for a hardcoded value */}
					{/* eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
					<div style={{ paddingTop: '220px' }}>
						<Spinner testId="opsgenie-schedule.loading" size="large" />
					</div>
				</Flex>
			);
		if (associatedOpsgenieTeamError) return dataLoadingErrorAgg();
		return showScheduleAgg();
	};

	return (
		<Box
			// Note: this test ID is used in a Pollinator check
			testId="opsgenie-schedule.try-opsgenie-schedule-view-container"
			id="try-opsgenie-schedule-view-container"
			xcss={viewContainerStyles}
		>
			<ContextualAnalyticsData attributes={{ simplified }}>
				<FireTrackAnalytics
					eventName="onCallScheduleContainer viewed"
					attributes={{ isPreExpand }}
				/>
				<PageTopBar
					breadcrumbs={<BreadcrumbRenderer />}
					actions={
						<ButtonGroup>
							{getWillShowNav3() && (
								<Button
									appearance="subtle-link"
									onClick={onSubmitFeedbackClick}
									ref={feedbackButtonRef}
									iconBefore={<FeedbackIcon label={formatMessage(messages.feedbackIconLabel)} />}
								>
									{formatMessage(messages.leaveFeedback)}
								</Button>
							)}

							{/* in New Nav always show MoreMenu as Feedback button is now in the dropdown */}
							{/* in Old Nav show MoreMenu to admins (always) and project-admins (post-expand) */}
							{(getWillShowNav4() || expandMode !== 'none') && (
								<MoreMenu
									{...{
										baseUrl,
										projectKey,
										projectId,
										simplified,
										expandMode,
									}}
								/>
							)}
						</ButtonGroup>
					}
				>
					{formatMessage(messages.opsgenieSchedulePageHeader)}
				</PageTopBar>

				<DocumentTitle title={formatMessage(messages.opsgenieSchedulePageTitle)} />
				<>
					{getWillShowNav4() ? (
						renderSchedule()
					) : (
						<ProjectBreadcrumb {...{ baseUrl, projectKey, projectName }}>
							{renderSchedule()}
						</ProjectBreadcrumb>
					)}
				</>
				{isPreExpand && <TryItNowComponent isAdmin={isAdmin} />}
			</ContextualAnalyticsData>
		</Box>
	);
};

OpsgenieScheduleView.defaultProps = {
	isPreExpand: false,
};

const viewContainerStyles = xcss({
	minWidth: '684px',
	minHeight: '640px',
	paddingBottom: 'space.300',
});

const descriptionLineStyles = xcss({
	marginTop: 'space.0',
});

export default OpsgenieScheduleView;
